import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { SortingState, PaginationState } from "@tanstack/react-table";
import { AsyncConfig } from "./types";

// Type for API response
export interface PaginatedApiResponse<DataType> {
  results: DataType[];
  pageCount: number;
  total?: number;
}

// Type for getData function
export type PaginatedApi<DataType> = (
  pageIndex: number,
  pageSize: number,
  sorting: SortingState,
  search: string,
  filters?: any
) => Promise<PaginatedApiResponse<DataType>>;

/**
 * Hook to manage async table configuration
 * Replicates the behavior of TableUtil.useAsyncConfig from @tyrannosaurustech/ui
 */
export function useAsyncConfig<DataType extends object>(
  getData: PaginatedApi<DataType>,
  initialPageSize = 50,
  initialSearchString = ""
): [
  {
    asyncConfig: AsyncConfig<DataType>;
    data: DataType[];
    loading: boolean;
    error: string;
    total?: number;
  },
  () => Promise<void>
] {
  // Use ref to store getData function to prevent infinite loops
  const getDataRef = useRef(getData);
  getDataRef.current = getData;

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: initialPageSize,
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [search, setSearch] = useState(initialSearchString);
  const [filters, setFilters] = useState<any>(undefined);
  const [data, setData] = useState<DataType[]>([]);
  const [pageCount, setPageCount] = useState(0);
  const [total, setTotal] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Track request ID to prevent race conditions - only the latest request updates state
  const requestIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    // Increment request ID and capture it for this request
    const currentRequestId = ++requestIdRef.current;

    setLoading(true);
    setError("");
    try {
      const result = await getDataRef.current(
        pagination.pageIndex,
        pagination.pageSize,
        sorting,
        search,
        filters
      );

      // Only update state if this is still the latest request
      if (currentRequestId !== requestIdRef.current) {
        return; // Stale request - ignore results
      }

      setData(result.results || []);
      setPageCount(result.pageCount || 0);
      setTotal(result.total);
    } catch (e) {
      // Only update error state if this is still the latest request
      if (currentRequestId !== requestIdRef.current) {
        return;
      }
      setError(String(e));
      setData([]);
      setPageCount(0);
    } finally {
      // Only clear loading if this is still the latest request
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [pagination, sorting, search, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = useCallback((searchStr: string, newFilters?: any[]) => {
    setSearch(searchStr);
    if (newFilters !== undefined) {
      setFilters(newFilters);
    }
    // Reset to first page on search/filter change
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  // Memoize callbacks to prevent infinite loops
  const onPaginationChange = useCallback((updater: any) => {
    setPagination(updater);
  }, []);

  const onSortingChange = useCallback((updater: any) => {
    setSorting(updater);
  }, []);

  // Memoize asyncConfig to prevent infinite re-renders
  const asyncConfig: AsyncConfig<DataType> = useMemo(
    () => ({
      pageIndex: pagination.pageIndex,
      pageSize: pagination.pageSize,
      onPaginationChange,
      onSortingChange,
      onSearch: handleSearch,
      sort: sorting,
      pageCount,
      facets: {
        search,
        sort: sorting,
        filters: filters,
      },
    }),
    [
      pagination.pageIndex,
      pagination.pageSize,
      onPaginationChange,
      onSortingChange,
      handleSearch,
      sorting,
      pageCount,
      search,
      filters,
    ]
  );

  return [
    {
      asyncConfig,
      data,
      loading,
      error,
      total,
    },
    fetchData,
  ];
}
